import React from 'react';
import { Card, Grid, Select } from 'antd';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceArea,
} from 'recharts';
import { STATUS_COLORS } from '../../utils/reportStyles';

const { useBreakpoint } = Grid;

// Traffic-light zone definitions using shared palette
const ZONES = [
  { y1: 0, y2: 10, fill: STATUS_COLORS.Negro.hex, fillOpacity: 0.15, label: 'Negro' },
  { y1: 10, y2: 30, fill: STATUS_COLORS.Rojo.hex, fillOpacity: 0.15, label: 'Rojo' },
  { y1: 30, y2: 60, fill: STATUS_COLORS.Amarillo.hex, fillOpacity: 0.20, label: 'Amarillo' },
  { y1: 60, y2: 80, fill: STATUS_COLORS.Verde.hex, fillOpacity: 0.20, label: 'Verde' },
  { y1: 80, y2: 115, fill: STATUS_COLORS.Azul.hex, fillOpacity: 0.20, label: 'Azul' },
];

// Ampliar paleta de colores para distinguir múltiples combinaciones SKU×Proveedor
const COLORS = [
  '#e63946', '#2196F3', '#FF9800', '#9C27B0', '#00BCD4', '#4CAF50',
  '#F44336', '#3F51B5', '#FF5722', '#673AB7', '#009688', '#8BC34A',
  '#CDDC39', '#FFEB3B', '#FFC107', '#795548', '#607D8B', '#E91E63',
  '#03A9F4', '#4DB6AC', '#FF7043', '#9575CD', '#4DD0E1', '#81C784',
];

/**
 * Generate a consistent color from a string key using golden ratio hash.
 */
const colorForKey = (key) => {
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = key.charCodeAt(i) + ((hash << 5) - hash);
  }
  return COLORS[Math.abs(hash) % COLORS.length];
};

/**
 * Determine the traffic-light zone name from a percentage value.
 */
const getZoneName = (pct) => {
  if (pct <= 10) return 'Negro';
  if (pct <= 30) return 'Rojo';
  if (pct <= 60) return 'Amarillo';
  if (pct <= 80) return 'Verde';
  return 'Azul';
};

/**
 * Format a YYYY-MM-DD date as DD/MM
 */
const formatShortDate = (dateStr) => {
  if (!dateStr) return '';
  const [year, month, day] = dateStr.split('-');
  return `${day}/${month}`;
};

/**
 * Build a composite key from CodeSap and Nit.
 */
const makeKey = (codeSap, nit) => `${codeSap}__${nit}`;

/**
 * Extract CodeSap from a composite key.
 */
const getSku = (compositeKey) => compositeKey.split('__')[0];

/**
 * Custom tooltip showing date, each SKU×Provider's % and zone.
 */
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;

  return (
    <div style={{
      background: 'white',
      border: '1px solid #ccc',
      padding: 10,
      borderRadius: 6,
      boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
      maxWidth: 400,
      maxHeight: 300,
      overflowY: 'auto',
    }}>
      <p style={{ margin: '0 0 6px 0', fontWeight: 600 }}>
        {formatShortDate(label)}
      </p>
      {payload.map((entry) => {
        const pct = entry.value;
        if (pct == null) return null;
        const zona = getZoneName(pct);
        return (
          <p key={entry.dataKey} style={{ color: entry.color, margin: '2px 0', fontSize: 13 }}>
            {entry.name}: <strong>{pct?.toFixed(1)}%</strong> ({zona})
          </p>
        );
      })}
    </div>
  );
};

/**
 * SkuInventoryLevelChart
 *
 * LineChart with colored ReferenceArea zones (traffic-light) and one Line per
 * SKU×Provider combination, showing PercentInventory over time.
 *
 * @param {Object} props
 * @param {Array} props.data - Raw records from /skusRecords with RecordDate, CodeSap,
 *   SkuDescription, PercentInventory, ColorLabel, Nit, ProjectName, etc.
 * @param {Array} [props.selectedSkus] - Array of CodeSap values to display. Empty = show all (top 15 combos).
 * @param {Function} [props.onSkuChange] - Callback when the SKU selection changes.
 */
function SkuInventoryLevelChart({ data, selectedSkus = [], onSkuChange = null }) {
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  // Build composite keys and metadata from all data
  const { allCompositeKeys, keyMeta, uniqueCodeSaps, skuDescriptions } = React.useMemo(() => {
    if (!data || data.length === 0) {
      return { allCompositeKeys: [], keyMeta: {}, uniqueCodeSaps: [], skuDescriptions: {} };
    }

    const meta = {};
    const codeSapSet = new Set();
    const descriptions = {};

    data.forEach((d) => {
      const key = makeKey(d.CodeSap, d.Nit);
      codeSapSet.add(d.CodeSap);
      descriptions[d.CodeSap] = d.SkuDescription || d.CodeSap;

      if (!meta[key]) {
        meta[key] = {
          codeSap: d.CodeSap,
          nit: d.Nit,
          projectName: d.ProjectName || d.Nit,
          description: d.SkuDescription || d.CodeSap,
          recordCount: 0,
        };
      }
      meta[key].recordCount++;
    });

    // Sort composite keys: by CodeSap asc, then projectName asc
    const keys = Object.keys(meta).sort((a, b) => {
      const skuComp = meta[a].codeSap.localeCompare(meta[b].codeSap);
      if (skuComp !== 0) return skuComp;
      return (meta[a].projectName || '').localeCompare(meta[b].projectName || '');
    });

    return {
      allCompositeKeys: keys,
      keyMeta: meta,
      uniqueCodeSaps: [...codeSapSet].sort(),
      skuDescriptions: descriptions,
    };
  }, [data]);

  // Active composite keys: expand selectedSkus (CodeSap) to all their SKU×Provider combos
  const activeKeys = React.useMemo(() => {
    if (selectedSkus.length > 0) {
      // Show all provider combos for the selected SKUs
      return allCompositeKeys.filter((key) => selectedSkus.includes(getSku(key)));
    }

    // Default: top 15 composite keys by record count
    return allCompositeKeys
      .slice()
      .sort((a, b) => (keyMeta[b]?.recordCount || 0) - (keyMeta[a]?.recordCount || 0))
      .slice(0, 15);
  }, [selectedSkus, allCompositeKeys, keyMeta]);

  // Transform raw data: pivot by date, one column per active composite key
  const chartData = React.useMemo(() => {
    if (!data || data.length === 0 || activeKeys.length === 0) return [];

    const dates = [...new Set(data.map((d) => d.RecordDate))].sort();

    return dates.map((date) => {
      const entry = { fecha: date };
      activeKeys.forEach((key) => {
        const { codeSap, nit } = keyMeta[key];
        const record = data.find((d) => d.RecordDate === date && d.CodeSap === codeSap && d.Nit === nit);
        entry[key] = record ? record.PercentInventory : null;
      });
      return entry;
    });
  }, [data, activeKeys, keyMeta]);

  // Select options for SKU picker (still by CodeSap, not composite key)
  const skuOptions = React.useMemo(() => {
    return uniqueCodeSaps.map((codeSap) => ({
      value: codeSap,
      label: `${codeSap} - ${skuDescriptions[codeSap] || ''}`,
    }));
  }, [uniqueCodeSaps, skuDescriptions]);

  // Tick interval: show ~15 labels max
  const tickInterval = React.useMemo(() => {
    if (chartData.length <= 15) return 0;
    return Math.floor(chartData.length / 15);
  }, [chartData.length]);

  // Empty state
  if (!chartData || chartData.length === 0) {
    return (
      <Card
        bordered={false}
        className="shadow-sm"
        style={{ height: '100%', borderRadius: '12px', backgroundColor: '#fafafa' }}
      >
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 300,
          color: '#9ca3af',
          textAlign: 'center',
          padding: 20,
        }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>{'📊'}</div>
          <div style={{ fontSize: isMobile ? 14 : 16, fontWeight: 500, marginBottom: 8 }}>
            Datos insuficientes
          </div>
          <div style={{ fontSize: isMobile ? 12 : 13 }}>
            Se requieren datos de inventario para mostrar el nivel por SKU
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card
      bordered={false}
      className="shadow-sm"
      style={{
        height: '100%',
        borderRadius: '12px',
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
      }}
      bodyStyle={{ padding: isMobile ? 16 : 20 }}
      title={
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '12px',
        }}>
          <span style={{ fontSize: '18px', fontWeight: 600, color: '#111827' }}>
            Nivel de stock por producto
          </span>
          <Select
            mode="multiple"
            style={{ minWidth: '200px', maxWidth: '400px' }}
            placeholder={selectedSkus.length === 0 ? 'Todos los SKUs (top 15)' : 'Seleccionar SKUs'}
            value={selectedSkus}
            onChange={(values) => onSkuChange?.(values)}
            allowClear
            showSearch
            maxTagCount="responsive"
            options={skuOptions}
            optionFilterProp="label"
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
          />
        </div>
      }
    >
      <ResponsiveContainer width="100%" height={480}>
        <LineChart
          data={chartData}
          margin={{ top: 10, right: 40, left: 50, bottom: 80 }}
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} />

          {/* Traffic-light background zones */}
          {ZONES.map(({ y1, y2, fill, fillOpacity }) => (
            <ReferenceArea
              key={`${y1}-${y2}`}
              y1={y1}
              y2={y2}
              fill={fill}
              fillOpacity={fillOpacity}
            />
          ))}

          <XAxis
            dataKey="fecha"
            tickFormatter={formatShortDate}
            tickLine={false}
            angle={-30}
            textAnchor="end"
            height={80}
            interval={tickInterval}
            style={{ fontSize: isMobile ? 9 : 11, color: '#6b7280' }}
          />
          <YAxis
            domain={[0, 115]}
            tickFormatter={(v) => `${v}%`}
            style={{ fontSize: isMobile ? 9 : 11, color: '#6b7280' }}
            label={{
              value: '% Inventario / ROP',
              angle: -90,
              position: 'insideLeft',
              offset: -10,
              style: { fontSize: 12, fill: '#6b7280' },
            }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            verticalAlign="bottom"
            layout="horizontal"
            wrapperStyle={{
              fontSize: isMobile ? 10 : 12,
              paddingTop: 8,
              maxWidth: '100%',
              overflow: 'hidden',
            }}
            formatter={(value) => {
              const meta = keyMeta[value];
              const label = meta
                ? `${meta.codeSap} - ${meta.projectName}`
                : value;
              return (
                <span
                  style={{
                    maxWidth: 180,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    display: 'inline-block',
                    verticalAlign: 'middle',
                  }}
                  title={label}
                >
                  {label}
                </span>
              );
            }}
          />

          {/* One line per active SKU×Provider combination */}
          {activeKeys.map((key, i) => {
            const meta = keyMeta[key];
            return (
              <Line
                key={key}
                dataKey={key}
                name={meta ? `${meta.description} (${meta.projectName})` : key}
                stroke={colorForKey(key)}
                strokeWidth={2}
                dot={{ r: 2 }}
                type="monotone"
                connectNulls={false}
              />
            );
          })}
        </LineChart>
      </ResponsiveContainer>
    </Card>
  );
}

export default SkuInventoryLevelChart;
